import {
  Breadcrumb,
  CollapseCompact,
  formatDate,
  LoadingPage,
  SidePane,
} from "@endya-conformite/ui-kit";
import { SidebarDroite } from "@src/components/sidebar/SidebarDroite/SidebarDroite";
import { useBreadcrumb } from "@src/context/BreadcrumbContext";
import { PersonneAVerifierContext } from "@src/context/PersonneContext";
import { useCQAxeWithFilesPaginatedQuery } from "@src/data/controle-quinquennal/hook/useCQAxeWithFilesQuery";
import { useCQConversationQuery } from "@src/data/controle-quinquennal/hook/useCQConversationQuery";
import { useCQWithAxesQuery } from "@src/data/controle-quinquennal/hook/useCQWithAxesQuery";
import { useEntreprise } from "@src/data/entreprise/hook/useEntreprise";
import { isControleQuinquennalReadOnly } from "@src/helper/ticket.helper";
import {
  axeTypeToLabel,
  CQParams,
} from "@src/pages/controle-quinquennal/controleQuinquennal.helper";
import classNames from "classnames";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Conversation } from "../sidebar/SidebarInformationsEntreprise/Conversation";
import { SidebarInformationsEntreprise } from "../sidebar/SidebarInformationsEntreprise/SidebarInformationsEntreprise";
import styles from "./AxePageTemplate.module.scss";

export type MainContentPageTemplateProps = {
  children: ReactNode;
  actions?: ReactNode;
};

export function AxePageTemplateContent({
  idEntreprise,
  idCQ,
  idAxe,
  children,
  actions,
}: CQParams & MainContentPageTemplateProps) {
  const breadcrumb = useBreadcrumb();
  const [sidePaneState, setSidePaneState] = useState(false);
  const storageKey = `selected-personne-${idCQ}-${idAxe}`;
  const [isLeftCollapsed, setIsLeftCollapsed] = useState(false);
  const [isRightCollapsed, setIsRightCollapsed] = useState(false);
  const [selectedPersonneId, setSelectedPersonneId] = useState<string>(
    sessionStorage.getItem(storageKey) ?? ""
  );

  const [openConversations, setOpenConversations] = useState<
    Record<string, boolean>
  >({});

  const { data: cq } = useCQWithAxesQuery(idEntreprise);

  const { data: axeData } = useCQAxeWithFilesPaginatedQuery(idCQ, idAxe);

  const handlePersonneSelected = useCallback(
    (id: string) => {
      if (id) {
        sessionStorage.setItem(storageKey, id);
      } else {
        sessionStorage.removeItem(storageKey);
      }
      setSelectedPersonneId(id);
    },
    [storageKey]
  );

  const personneContextValue = useMemo(
    () => ({ selectedPersonneId, onPersonneSelected: handlePersonneSelected }),
    [selectedPersonneId, handlePersonneSelected]
  );

  const axeName = axeData?.axe?.type ? axeTypeToLabel[axeData.axe.type] : "";

  const { data: entreprise } = useEntreprise(cq?.millesime, idEntreprise);

  const { data: conversations } = useCQConversationQuery(idCQ, idAxe, axeName);

  const toggleConversation = (id: string) => {
    setOpenConversations((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const formattedConversations = useMemo(
    () =>
      conversations?.map((conversation) => ({
        ...conversation,
        formattedDate: conversation.remediation
          ? formatDate(new Date(conversation.remediation.creationDate))
          : undefined,
      })),
    [conversations]
  );

  if (
    !idCQ ||
    !idAxe ||
    !idEntreprise ||
    !axeData?.axe ||
    !entreprise ||
    !conversations ||
    !cq
  )
    return (
      <div>
        <LoadingPage withText={false} />
      </div>
    );

  return (
    <>
      <div className={styles.container}>
        <SidebarInformationsEntreprise
          axeData={axeData.axe}
          setSidePaneState={setSidePaneState}
          entreprise={entreprise}
          conversations={conversations}
          millesime={cq.millesime}
          collapsed={isLeftCollapsed}
          onToggle={() => setIsLeftCollapsed((v) => !v)}
        />
        <div className={styles.content}>
          {breadcrumb && (
            <div className={styles.breadcrumbContainer}>
              <Breadcrumb variant="bald" {...breadcrumb} />
            </div>
          )}
          <div
            className={classNames(styles.scrollBody, {
              [styles.withActions]: !!actions,
            })}
          >
            <PersonneAVerifierContext.Provider value={personneContextValue}>
              {children}
            </PersonneAVerifierContext.Provider>
          </div>
        </div>
        {actions && <div className={styles.actionsBar}>{actions}</div>}

        <SidebarDroite
          idCq={idCQ}
          idAxe={idAxe}
          idEntreprise={idEntreprise}
          millesime={cq?.millesime}
          siren={entreprise.siren}
          axeName={axeName}
          onPersonneSelected={handlePersonneSelected}
          selectedPersonneId={selectedPersonneId}
          collapsed={isRightCollapsed}
          onToggle={() => setIsRightCollapsed((v) => !v)}
          isReadOnly={isControleQuinquennalReadOnly(cq.pipelineStage)}
        />
      </div>
      <SidePane
        isOpen={sidePaneState}
        setIsOpen={setSidePaneState}
        title="Historique des commentaires"
      >
        {() =>
          formattedConversations &&
          formattedConversations.map((conversation) => {
            const conversationKey =
              conversation.remediation?.id ?? "orphan-notes";
            const conversationDate =
              conversation.remediation?.creationDate ?? "orphan-notes";
            const conversationTitle = conversation.formattedDate
              ? `Conversation du ${conversation.formattedDate}`
              : "Commentaire adhérent";
            return (
              <CollapseCompact
                key={conversationKey}
                isOpen={openConversations[conversationDate] || false}
                onToggle={() => toggleConversation(conversationDate)}
                title={conversationTitle}
              >
                <Conversation
                  siren={entreprise.siren}
                  millesime={cq.millesime}
                  conversation={conversation}
                  showBorder
                />
              </CollapseCompact>
            );
          })
        }
      </SidePane>
      {actions && <div className={styles.actionsBar}>{actions}</div>}
    </>
  );
}

export function AxePageTemplate({
  children,
  actions,
}: MainContentPageTemplateProps) {
  const { idEntreprise, idCQ, idAxe } = useParams<CQParams>();
  if (!idEntreprise || !idCQ || !idAxe)
    return <div>Impossible d&apos;afficher la page</div>;
  return (
    <AxePageTemplateContent
      idEntreprise={idEntreprise}
      idCQ={idCQ}
      idAxe={idAxe}
      actions={actions}
    >
      {children}
    </AxePageTemplateContent>
  );
}
